import React from 'react';
import { LocationColumn } from './LocationColumn';
import { TrainingBlock } from './TrainingBlock';
interface CalendarViewProps {
  viewMode: 'day' | 'week';
  currentDate: Date;
  locations: any[];
  trainingSessions: any[];
  trainingTypes: any[];
  instructors: any[];
  onTrainingClick: (training: any) => void;
}
export function CalendarView({
  viewMode,
  currentDate,
  locations,
  trainingSessions,
  trainingTypes,
  instructors,
  onTrainingClick
}: CalendarViewProps) {
  // Constants for time display
  const startHour = 8; // 8:00
  const endHour = 18; // 18:00
  const hourHeight = 100; // height in pixels for one hour
  const calendarHeight = (endHour - startHour) * hourHeight;
  // Function to get dates for the current view
  const getDatesForView = () => {
    const dates = [];
    if (viewMode === 'day') {
      dates.push(new Date(currentDate));
    } else {
      // Week view - show Monday to Friday
      const startOfWeek = new Date(currentDate);
      const day = currentDate.getDay();
      const diff = currentDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
      startOfWeek.setDate(diff);
      for (let i = 0; i < 5; i++) {
        // Monday to Friday
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        dates.push(date);
      }
    }
    return dates;
  };
  // Get dates for the current view
  const viewDates = getDatesForView();
  // Filter trainings for the current view dates
  const getTrainingsForDate = (date: Date, locationId: number) => {
    return trainingSessions.filter(session => {
      const sessionDate = new Date(session.startTime);
      return sessionDate.getDate() === date.getDate() && sessionDate.getMonth() === date.getMonth() && sessionDate.getFullYear() === date.getFullYear() && session.locationId === locationId;
    });
  };
  // Function to format time (e.g., "09:00")
  const formatTime = (hour: number) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };
  // Function to check if a date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };
  // Format day header (e.g., "Ma 12")
  const formatDayHeader = (date: Date) => {
    const dayNames = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
    return `${dayNames[date.getDay()]} ${date.getDate()}`;
  };
  // Detect overlapping trainings
  const detectOverlaps = () => {
    const overlaps = [];
    // Check for location overlaps (double booking of same location)
    locations.forEach(location => {
      viewDates.forEach(date => {
        const dateTrainings = getTrainingsForDate(date, location.id);
        for (let i = 0; i < dateTrainings.length; i++) {
          for (let j = i + 1; j < dateTrainings.length; j++) {
            const training1 = dateTrainings[i];
            const training2 = dateTrainings[j];
            // Check if time ranges overlap
            if (training1.startTime <= training2.endTime && training1.endTime >= training2.startTime || training2.startTime <= training1.endTime && training2.endTime >= training1.startTime) {
              overlaps.push({
                type: 'location',
                locationId: location.id,
                trainings: [training1.id, training2.id]
              });
            }
          }
        }
      });
    });
    // Check for instructor overlaps (instructor assigned to multiple trainings at same time)
    instructors.forEach(instructor => {
      const instructorTrainings = trainingSessions.filter(session => session.instructorId === instructor.id);
      for (let i = 0; i < instructorTrainings.length; i++) {
        for (let j = i + 1; j < instructorTrainings.length; j++) {
          const training1 = instructorTrainings[i];
          const training2 = instructorTrainings[j];
          // Check if time ranges overlap
          if (training1.startTime <= training2.endTime && training1.endTime >= training2.startTime || training2.startTime <= training1.endTime && training2.endTime >= training1.startTime) {
            // Check if they're on the same day
            const date1 = new Date(training1.startTime);
            const date2 = new Date(training2.startTime);
            if (date1.getDate() === date2.getDate() && date1.getMonth() === date2.getMonth() && date1.getFullYear() === date2.getFullYear()) {
              overlaps.push({
                type: 'instructor',
                instructorId: instructor.id,
                trainings: [training1.id, training2.id]
              });
            }
          }
        }
      }
    });
    return overlaps;
  };
  const overlaps = detectOverlaps();
  // Check if a training has an overlap
  const hasOverlap = (trainingId: number) => {
    return overlaps.some(overlap => overlap.trainings.includes(trainingId));
  };
  return <div className="relative" data-id="element-3024">
      {/* Time column */}
      <div className="flex" data-id="element-3025">
        <div className="w-16 flex-shrink-0" data-id="element-3026">
          <div className="h-10 border-b border-gray-200" data-id="element-3027"></div>
          <div style={{
          height: `${calendarHeight}px`
        }} className="relative" data-id="element-3028">
            {Array.from({
            length: endHour - startHour
          }).map((_, index) => <div key={index} className="absolute border-b border-gray-200 w-full text-xs text-gray-500 flex items-end justify-end pr-2" style={{
            top: `${index * hourHeight}px`,
            height: `${hourHeight}px`
          }} data-id="element-3029">
                {formatTime(startHour + index)}
              </div>)}
          </div>
        </div>
        {/* Calendar grid */}
        <div className="flex-1 overflow-x-auto" data-id="element-3030">
          <div className="flex" data-id="element-3031">
            {viewDates.map((date, dateIndex) => <div key={dateIndex} className="flex-1 min-w-[200px]" data-id="element-3032">
                <div className={`h-10 border-b border-gray-200 flex items-center justify-center font-medium ${isToday(date) ? 'bg-[#B61C1C] text-white' : 'bg-gray-50 text-gray-700'}`} data-id="element-3033">
                  {formatDayHeader(date)}
                </div>
                <div className="flex" data-id="element-3034">
                  {locations.map(location => <LocationColumn key={`${dateIndex}-${location.id}`} location={location} date={date} trainings={getTrainingsForDate(date, location.id)} trainingTypes={trainingTypes} instructors={instructors} startHour={startHour} endHour={endHour} hourHeight={hourHeight} onTrainingClick={onTrainingClick} hasOverlap={hasOverlap} data-id="element-3035" />)}
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}